// LoginPage.tsx
import React, { useState, useCallback } from 'react';
import api from './api';
import { useNavigate } from 'react-router-dom';
import { setToken, setRefreshToken, setUser } from './config';
import './App.css';

interface User {
  id: number;
  username: string;
  role: string;
  can_view_dashboard?: boolean;
  can_view_sales?: boolean;
  can_create_sale?: boolean;
  can_edit_sale?: boolean;
  can_delete_sale?: boolean;
  can_view_products?: boolean;
  can_create_product?: boolean;
  can_edit_product?: boolean;
  can_delete_product?: boolean;
  can_view_categories?: boolean;
  can_create_category?: boolean;
  can_edit_category?: boolean;
  can_delete_category?: boolean;
  can_view_suppliers?: boolean;
  can_create_supplier?: boolean;
  can_edit_supplier?: boolean;
  can_delete_supplier?: boolean;
  can_view_invoices?: boolean;
  can_create_invoice?: boolean;
  can_edit_invoice?: boolean;
  can_delete_invoice?: boolean;
  can_view_expenses?: boolean;
  can_create_expense?: boolean;
  can_edit_expense?: boolean;
  can_delete_expense?: boolean;
  can_view_salaries?: boolean;
  can_create_salary?: boolean;
  can_edit_salary?: boolean;
  can_delete_salary?: boolean;
  can_view_reports?: boolean;
  can_view_profit?: boolean;
  can_view_movements?: boolean;
  can_manage_users?: boolean;
  can_manage_settings?: boolean;
  created_at?: string;
  last_login?: string;
}

// ✅ تحديث واجهة LoginResponse لتشمل token و refresh_token
interface LoginResponse {
  success: boolean;
  user?: User;
  token?: string;
  refresh_token?: string;
  message?: string;
}

function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // التحقق من صحة المدخلات
  const validateInputs = useCallback((): boolean => {
    if (!username.trim()) {
      setError('❌ اسم المستخدم مطلوب');
      return false;
    }
    if (!password.trim()) {
      setError('❌ كلمة السر مطلوبة');
      return false;
    }
    if (password.length < 4) {
      setError('❌ كلمة السر يجب أن تكون 4 أحرف على الأقل');
      return false;
    }
    return true;
  }, [username, password]);

  const handleLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    // التحقق من المدخلات
    if (!validateInputs()) return;
    
    setLoading(true);
    setError('');

    try {
      console.log('🚀 محاولة تسجيل الدخول:', { username: username.trim(), password: '***' });
      
      const response = await api.post<LoginResponse>(
        '/api/login',  // ✅ استخدام المسار النسبي لأن baseURL محدد في api
        {
          username: username.trim(),
          password: password.trim()
        },
        {
          timeout: 10000,
        }
      );

      console.log('✅ رد السيرفر:', response.data);

      // ✅ التحقق من وجود token في الرد
      if (response.data.success && response.data.user && response.data.token) {
        // تخزين بيانات المستخدم
        setUser(response.data.user);
        
        // ✅ تخزين token
        setToken(response.data.token);
        
        // ✅ تخزين refresh_token لو موجود
        if (response.data.refresh_token) {
          setRefreshToken(response.data.refresh_token);
        }
        
        // تخزين وقت آخر تسجيل دخول
        sessionStorage.setItem('last_login', new Date().toISOString());
        
        console.log('🔐 تم تسجيل الدخول بنجاح');
        
        // التوجيه للصفحة الرئيسية
        navigate('/');
      } else {
        // لو مفيش token، نعرض رسالة الخطأ
        setError(response.data.message || '❌ اسم المستخدم أو كلمة السر غير صحيحة');
      }
    } catch (err: any) {
      console.error('❌ خطأ في تسجيل الدخول:', err);
      
      if (err.code === 'ECONNABORTED') {
        setError('⚠️ انتهت مهلة الاتصال - تأكد من أن الخادم شغال');
      } else if (err.response) {
        // خطأ من السيرفر
        const errorMessage = err.response.data?.detail || 
                           err.response.data?.message || 
                           `خطأ في السيرفر (${err.response.status})`;
        setError(`❌ ${errorMessage}`);
      } else if (err.request) {
        // لا يوجد رد من السيرفر
        setError('❌ لا يمكن الاتصال بالخادم - تأكد من تشغيل الخادم');
      } else {
        // خطأ في إعداد الطلب
        setError('❌ حدث خطأ غير متوقع: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [username, password, navigate, validateInputs]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleLogin(e as any);
    }
  }, [handleLogin, loading]);

  return (
    <div className="App" dir="rtl" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '10px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        width: '100%',
        maxWidth: '400px',
        animation: 'fadeIn 0.5s ease'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '10px' }}>🏪</span>
          <h1 style={{ color: '#4CAF50', margin: 0 }}>نظام إدارة المتجر</h1>
          <p style={{ color: '#666', marginTop: '10px' }}>تسجيل الدخول</p>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#ffebee',
            color: '#f44336',
            padding: '12px 16px',
            borderRadius: '5px',
            marginBottom: '20px',
            textAlign: 'center',
            border: '1px solid #f44336',
            fontWeight: 'bold',
            animation: 'shake 0.3s ease'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} style={{ display: 'grid', gap: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              اسم المستخدم:
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={handleKeyPress}
              required
              disabled={loading}
              maxLength={50}
              autoFocus
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #ddd',
                borderRadius: '5px',
                fontSize: '16px',
                transition: 'all 0.3s',
                opacity: loading ? 0.6 : 1,
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
              placeholder="أدخل اسم المستخدم"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
              كلمة السر:
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                required
                disabled={loading}
                maxLength={50}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px',
                  transition: 'all 0.3s',
                  opacity: loading ? 0.6 : 1,
                  outline: 'none',
                  paddingLeft: '40px'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
                placeholder="أدخل كلمة السر"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: '5px',
                  color: '#666'
                }}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
              4 أحرف على الأقل
            </small>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '14px',
              backgroundColor: loading ? '#ccc' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'all 0.3s',
              marginTop: '10px',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = '#45a049')}
            onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = '#4CAF50')}
          >
            {loading ? (
              <>
                <span className="loading-spinner" style={{ 
                  width: '20px', 
                  height: '20px', 
                  borderWidth: '3px',
                  borderColor: 'rgba(255,255,255,0.3)',
                  borderTopColor: 'white'
                }} />
                جاري تسجيل الدخول...
              </>
            ) : (
              <>
                <span>🔐</span>
                تسجيل الدخول
              </>
            )}
          </button>
        </form>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        .loading-spinner {
          display: inline-block;
          border: 3px solid #f3f3f3;
          border-radius: 50%;
          border-top: 3px solid #3498db;
          width: 20px;
          height: 20px;
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}

export default LoginPage;